import React from 'react';
import { useTranslation } from 'react-i18next';
import { Spinner } from '../../../../components/ui/Spinner';
import { PageLayout } from '../../../../components/ui/PageLayout';
import { FileText } from 'lucide-react';
import { useToast, ToastRenderer } from '../../../../hooks/useToast';
import { useBrandingSettings } from './hooks/useBrandingSettings';
import { useLogoUpload } from './hooks/useLogoUpload';
import BrandingToggle from './components/BrandingToggle';
import LogoUploadSection from './components/LogoUploadSection';
import HeaderSettings from './components/HeaderSettings';
import FooterSettings from './components/FooterSettings';
import PayslipPreview from './components/PayslipPreview';
import BrandingActions from './components/BrandingActions';

interface PayslipBrandingCardProps {
  onSettingsChange?: () => void;
}

const PayslipBrandingCard: React.FC<PayslipBrandingCardProps> = ({ onSettingsChange }) => {
  const { t } = useTranslation('payroll');
  const { toast, showToast, dismissToast } = useToast();

  const branding = useBrandingSettings(onSettingsChange, showToast, t);
  const logoUpload = useLogoUpload(
    branding.setBrandingSettings,
    onSettingsChange,
    showToast,
  );

  if (branding.isLoading) {
    return (
      <PageLayout
        title={t('Payslip Branding Options')}
        description={t('Customize logo, header text, and footer text for payslips')}
        icon={FileText}
        accentColor="emerald"
      >
        <div className="bg-card rounded-xl shadow-sm border border-border p-6 flex items-center justify-center min-h-[200px]">
          <Spinner size="lg" />
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title={t('Payslip Branding Options')}
      description={t('Customize logo, header text, and footer text for payslips')}
      icon={FileText}
      accentColor="emerald"
    >
      <div className="bg-card rounded-xl shadow-sm border border-border p-6">
      <ToastRenderer toast={toast} onDismiss={dismissToast} />

      <BrandingToggle
        enabled={branding.brandingSettings.enableBranding}
        onToggle={branding.toggleBranding}
      />

      {branding.brandingSettings.enableBranding && (
        <>
          <LogoUploadSection
            brandingSettings={branding.brandingSettings}
            setBrandingSettings={branding.setBrandingSettings}
            logoUpload={logoUpload}
          />

          <HeaderSettings
            settings={branding.brandingSettings.headerSettings}
            onUpdate={branding.updateHeaderSettings}
          />

          <FooterSettings
            settings={branding.brandingSettings.footerSettings}
            onUpdate={branding.updateFooterSettings}
          />

          <PayslipPreview settings={branding.brandingSettings} />
        </>
      )}

      <BrandingActions
        isSaving={branding.isSaving}
        onReset={branding.handleReset}
        onSave={branding.handleSave}
      />
      </div>
    </PageLayout>
  );
};

export default PayslipBrandingCard;
